import React, { memo, Fragment, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { createUseStyles, RadioGroup, Multiplier, Text, Advice } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import { getImportSchema, getImport, updateSchema, runImport } from 'lib/services/imports';
import { showErrorToast } from 'lib/errors';

import ModalConfirm from 'components/ModalConfirm';
import StepContent from '../StepContent';
import LimitsModal from '../LimitsModal';

import AvoidDuplicatesIcon from '../../icons/AvoidDuplicatesIcon';

import styles from './styles';
const useStyles = createUseStyles(styles, 'AvoidDuplicates');

const AvoidDuplicates = ({
    entityName,
    entityLogName,
    entityLabel,
    data,
    onImported,
    onBack,
    logEvent,
    onCancelConfirm,
    duplicatesForm,
    setDuplicatesForm,
    modalConfirmIsOpen,
    modalLimitsIsOpen,
    limits,
    onConfirmLimits,
    onCancelLimits,
}) => {
    const classes = useStyles();
    const isUsers = entityName === 'users';
    const { t } = useWeb5Context();
    const timeout = useRef();
    const fieldsQuestion = useRef();
    const [fieldOptions, setFieldOptions] = useState([]);
    const [schema, setSchema] = useState(data.schema);
    const [isProcessing, setIsProcessing] = useState(false);

    // Options
    const checkOptions = useMemo(
        () => [
            {
                label: t('cfm_action_yes'),
                value: true,
            },
            {
                label: t('cfm_action_no'),
                value: false,
            },
        ],
        [t],
    );

    const filterOption = useCallback(
        (option, searchText) => {
            let isValid =
                duplicatesForm.fields &&
                !duplicatesForm.fields.find((field) => field && field.value === option.value) &&
                schema.matchingSchema.find(
                    (field) => field.nameField && field.nameField === option.value,
                );

            if (option.label) {
                isValid = isValid && option.label.match(new RegExp(searchText, 'ig'));
            }

            return isValid;
        },
        [duplicatesForm.fields, schema],
    );

    const fieldSchema = useMemo(
        () => ({
            name: 'fields',
            type: 'select',
            placeholder: t('cfm_placeholder_choose_field'),
            attrs: {
                options: fieldOptions,
                overrides: {
                    'react-select': {
                        filterOption,
                    },
                },
            },
        }),
        [fieldOptions, filterOption, t],
    );

    const typeOptions = useMemo(
        () => [
            {
                label: t('cfm_label_duplicate_override_all'),
                value: '0',
            },
            {
                label: t('cfm_label_duplicate_right_empty_values'),
                value: '1',
            },
            {
                label: t('cfm_label_duplicate_right_keep_empty_values'),
                value: '2',
            },
        ],
        [t],
    );

    const pollingState = useCallback(
        (data, state, cb, cbErr) => {
            if (!Array.isArray(state)) state = [state];
            timeout.current = setTimeout(() => {
                getImport(data.id)
                    .then((result) => {
                        return state.includes(result.data.state)
                            ? cb && cb(result.data)
                            : pollingState(result.data, state, cb, cbErr);
                    })
                    .catch((error) => {
                        cbErr && cbErr();
                        showErrorToast(t, error);
                    });
            }, 2000);
        },
        [t],
    );

    useEffect(() => {
        return () => {
            if (timeout.current) clearTimeout(timeout.current);
        };
    }, []);

    // Get schema
    useEffect(() => {
        function formatData(data) {
            let fields = (data.duplicateSchema.fields || []).map((field) => ({
                label: field,
                value: field,
            }));

            setDuplicatesForm({
                enabled: data.duplicateSchema.enabled || true,
                type: data.duplicateSchema.type,
                fields: fields.length ? fields : [null],
            });

            setFieldOptions(
                data.matchingSchema.map((item) => ({
                    label: item.name,
                    value: item.nameField,
                })),
            );
        }

        // From previous step
        if (schema) {
            return formatData(schema);
        }

        // Updating schema
        if (data.state === 1) return pollingState(data, 1);

        // Run in progress
        if (data.state === 4) return pollingState(data, 4);

        getImportSchema(data.id)
            .then((result) => {
                formatData(result.data);
                setSchema(result.data);
            })
            .catch((error) => {
                console.error(error);
                showErrorToast(t, error);
                onBack();
            });
    }, [data, isUsers, onBack, pollingState, schema, t, setDuplicatesForm]);

    const onConfirm = useCallback(() => {
        setIsProcessing(true);
        logEvent({
            event: entityLogName,
            submodule: entityName,
            functionality: 'importCreateFinish',
        });

        let newDuplicateSchema = {
            ...schema.duplicateSchema,
            enabled: duplicatesForm.enabled,
            type: duplicatesForm.type,
        };

        let cleanFields = duplicatesForm.fields
            .filter((field) => field)
            .map((field) => field.value);

        if (cleanFields.length) newDuplicateSchema.fields = cleanFields;

        // Merge current matching values
        const newSchema = {
            ...schema,
            duplicateSchema: newDuplicateSchema,
        };

        updateSchema(data.id, newSchema)
            .then(() => runImport(data.id))
            .then(() => onImported(data.id))
            .catch((error) => {
                console.error(error);
                showErrorToast(t, error);
            });
    }, [
        logEvent,
        entityLogName,
        entityName,
        schema,
        duplicatesForm.enabled,
        duplicatesForm.type,
        duplicatesForm.fields,
        data,
        onImported,
        t,
    ]);

    // Form handlers
    const changeCheck = useCallback(
        (option) => {
            setDuplicatesForm({
                ...duplicatesForm,
                enabled: option,
            });
        },
        [setDuplicatesForm, duplicatesForm],
    );

    const changeType = useCallback(
        (option) => {
            setDuplicatesForm({
                ...duplicatesForm,
                type: parseInt(option, 10),
            });
        },
        [setDuplicatesForm, duplicatesForm],
    );

    const changeField = useCallback(
        (fields) => {
            setDuplicatesForm({
                ...duplicatesForm,
                fields,
            });

            if (fields.length !== duplicatesForm.fields.length && fieldsQuestion.current) {
                fieldsQuestion.current.scrollIntoView(true);
            }
        },
        [setDuplicatesForm, duplicatesForm],
    );

    const modalConfirmProps = useMemo(() => {
        return {
            onConfirm,
            onCancel: onCancelConfirm,
            isOpen: modalConfirmIsOpen,
            title: `${t('cfm_lable_confirm_import_title')} ${t('cfm_label_import_type_create', [
                entityLabel,
            ])}`,
            description: t('cfm_confirm_info_num_entity_create', [data.rows, entityLabel]),
            advice: t('label_integration_disclaimer_import_create'),
            writeXToConfirm: 'label_type_confirmation',
            passPhrase: t('cfm_label_create'),
            confirmText: t('cfm_action_confirm_import_create', [data.rows, entityLabel]),
            isConfirmLoading: isProcessing,
        };
    }, [data.rows, entityLabel, isProcessing, modalConfirmIsOpen, onCancelConfirm, onConfirm, t]);

    // Dynamic form
    const showFields = duplicatesForm.enabled && !isUsers;
    const showFounded =
        isUsers ||
        (duplicatesForm.enabled &&
            duplicatesForm.fields &&
            !!duplicatesForm.fields.find((field) => !!field));

    return (
        <Fragment>
            <div className={classes.stepWrapper}>
                <StepContent>
                    <div className={classes.row}>
                        <div className={classes.rowIcon}>
                            <AvoidDuplicatesIcon />
                        </div>
                        <Text bold>{t('cfm_label_duplicate_question_1')}</Text>
                    </div>
                    {isUsers && (
                        <Advice
                            type="info"
                            className={classes.duplicatesSubtitle}
                            showCollapse={false}
                        >
                            {t('cfm_label_warning_users_duplicate_management')}
                        </Advice>
                    )}
                    {!isUsers && (
                        <div className={classes.duplicatesSubtitle}>
                            <Text>{t('cfm_label_duplicate_desc_1')}</Text>
                        </div>
                    )}
                    {!isUsers && (
                        <div className={classes.duplicatesQuestion}>
                            <Text bold>{t('cfm_label_duplicate_question_2')}</Text>
                            <RadioGroup
                                options={checkOptions}
                                onChange={changeCheck}
                                value={duplicatesForm.enabled}
                            />
                        </div>
                    )}
                    {showFields && (
                        <div className={classes.duplicatesFieldsQuestion}>
                            <Text bold>{t('cfm_label_duplicate_question_3')}</Text>
                            <Text>{t('cfm_label_duplicate_question_3_desc1')}</Text>
                            <Text>{t('cfm_label_duplicate_question_3_desc2')}</Text>
                            <Multiplier
                                buttonLabel={t('cfm_action_add_field')}
                                value={duplicatesForm.fields}
                                schema={fieldSchema}
                                separator={false}
                                onChange={changeField}
                                classes={{
                                    item: classes.duplicatesFieldsItem,
                                    buttonContainer: classes.duplicatesFieldButton,
                                }}
                            />
                        </div>
                    )}
                    {showFounded && (
                        <div ref={fieldsQuestion} className={classes.duplicatesQuestion}>
                            <Text bold>{t('cfm_label_duplicate_question_4')}</Text>
                            <RadioGroup
                                options={typeOptions}
                                onChange={changeType}
                                value={duplicatesForm.type.toString()}
                                isFullWidth
                            />
                        </div>
                    )}
                </StepContent>
            </div>
            <LimitsModal
                isOpen={modalLimitsIsOpen}
                onConfirm={onConfirmLimits}
                onCancel={onCancelLimits}
                limits={limits}
                data={data}
            />
            <ModalConfirm {...modalConfirmProps} />
        </Fragment>
    );
};

AvoidDuplicates.propTypes = {};

export default memo(AvoidDuplicates);
